// API layer for the Sell Through Pictorial report (st_pictorial endpoints via pythonVmInstance).
// These functions build params and normalize responses; all component state stays in PictorialST.js.
import { format } from "date-fns";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { expandSizeLabelsForApi } from "./sizeUtils";

/** Debounce cascading filter-option requests (was 10ms — too chatty against BigQuery). */
export const FILTER_DEPS_DEBOUNCE_MS = 350;

/**
 * GET /st_pictorial/filter-options — master dropdown lists.
 * Returns the normalized filter-options object (with aging / st_percentage fallbacks).
 */
export const fetchPictorialFilterOptions = async (startDate, endDate) => {
	const params = {};
	if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
	if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
	const response = await pythonVmInstance.get("/st_pictorial/filter-options", { params });

	if (!response || !response.data) {
		console.error("Invalid API response:", response);
		throw new Error("Invalid API response");
	}

	const data = response.data;

	// The backend now returns data in the correct format
	return {
		departments: data.departments || [],
		subCategories: data.subCategories || [],
		brands: data.brands || [],
		subBrands: data.subBrands || [],
		styles: data.styles || [],
		colors: data.colors || [],
		sleeves: data.sleeves || [],
		divisions: data.divisions || [],
		seasons: data.seasons || [],
		fashionability: data.fashionability || [],
		occasions: data.occasions || [],
		palettes: data.palettes || [],
		fitTypes: data.fitTypes || [],
		fabricBlends: data.fabricBlends || [],
		sizes: data.sizes || [],
		nods: data.nods || [],
		channels: data.channels || [],
		stores: data.stores || [],
		cities: data.cities || [],
		states: data.states || [],
		regions: data.regions || [],
		tiers: data.tiers || [],
		aging: data.aging || [
			"0-30 days",
			"31-60 days",
			"61-90 days",
			"91-120 days",
			"121-180 days",
			"181-365 days",
			"365+ days",
		],
		st_percentage: data.st_percentage || [
			"0-10%",
			"11-20%",
			"21-30%",
			"31-40%",
			"41-50%",
			"51-60%",
			"61-70%",
			"71-80%",
			"81-90%",
			"91-100%",
		],
	};
};

/**
 * GET /st_pictorial/keys — server-side typeahead for the Key filter
 * (BRAND-DEPARTMENT-STYLE-COLOR-PALLETE_TYPE). Returns at most 50 matches for the prefix,
 * so the heavy distinct-key list never ships with /filter-options.
 */
export const fetchPictorialKeyOptions = async (query, signal) => {
	const params = {};
	const q = (query || "").trim();
	if (q) params.q = q;
	const response = await pythonVmInstance.get("/st_pictorial/keys", { params, signal });
	return response?.data?.keys || [];
};

/**
 * GET /st_pictorial/filter-dependencies — narrowed dropdown options for the active filters.
 * Returns the raw response data; the caller maps it onto the filter-options shape.
 */
export const fetchPictorialFilterDependencies = async (activeFilters, signal) => {
	const params = {};
	const skipKeys = new Set(["start_date", "end_date", "sort_field", "sort_direction"]);
	Object.entries(activeFilters || {}).forEach(([key, value]) => {
		if (skipKeys.has(key)) return;
		if (value && String(value).trim() !== "") {
			params[key] = value;
		}
	});

	// Size UI uses display labels (32/M); expand to raw snapshot SIZE values for BQ match.
	if (params.size) {
		params.size = expandSizeLabelsForApi(String(params.size).split(",")).join(",");
	}

	const response = await pythonVmInstance.get("/st_pictorial/filter-dependencies", {
		params,
		signal,
	});
	return response.data || {};
};

/**
 * GET /st_pictorial/company-totals — respects channel, store, city, state, region, tier
 * so header totals match the grid. Returns the normalized totals object.
 */
export const fetchPictorialCompanyTotals = async (startDate, endDate, filtersObj = {}) => {
	const formattedStartDate = format(startDate, "yyyy-MM-dd");
	const formattedEndDate = format(endDate, "yyyy-MM-dd");
	const params = { start_date: formattedStartDate, end_date: formattedEndDate };
	const locationKeys = ["channel", "store", "city", "state", "region", "tier"];
	locationKeys.forEach((key) => {
		const v = filtersObj[key];
		if (v != null && String(v).trim() !== "") params[key] = String(v).trim();
	});

	const response = await pythonVmInstance.get("/st_pictorial/company-totals", {
		params,
	});

	if (!response.data) return null;
	return {
		totalBuy: response.data.totalBuy ?? 0,
		totalSold: response.data.totalSold ?? 0,
		totalSoldOverall: response.data.totalSoldOverall ?? 0,
		balance: response.data.balance ?? 0,
		periodStPct: response.data.periodStPct ?? 0,
		totalStPct: response.data.totalStPct ?? 0,
		gpValue: response.data.gpValue ?? 0,
		mrpBuyValue: response.data.mrpBuyValue ?? 0,
		mrpSoldValue: response.data.mrpSoldValue ?? 0,
		rareRabbitSoldQty: response.data.rareRabbitSoldQty ?? 0,
		rareRabbitNetSalesValue: response.data.rareRabbitNetSalesValue ?? 0,
		rareRabbitGpValue: response.data.rareRabbitGpValue ?? 0,
		innervOnlyBills: response.data.innervOnlyBills ?? 0,
		innervWithOtherBills: response.data.innervWithOtherBills ?? 0,
	};
};

/**
 * GET /st_pictorial/products — main product fetch for the date range + filters.
 * Filters are passed as-is (excluding date/sort keys, which are handled client-side).
 * Returns the raw response data ({ products, metrics_channel, ... }).
 */
export const fetchPictorialProducts = async (startDate, endDate, filtersObj = {}, signal) => {
	const formattedStartDate = format(startDate, "yyyy-MM-dd");
	const formattedEndDate = format(endDate, "yyyy-MM-dd");

	const params = {
		start_date: formattedStartDate,
		end_date: formattedEndDate,
	};

	// Add filters to params (excluding sort parameters for client-side sorting).
	// Size stays client-side (matches size_soh_breakdown labels); do not send to /products.
	Object.entries(filtersObj).forEach(([key, value]) => {
		if (
			key !== "start_date" &&
			key !== "end_date" &&
			key !== "sort_field" &&
			key !== "sort_direction" &&
			key !== "size" &&
			value &&
			value.trim() !== ""
		) {
			params[key] = value;
		}
	});

	const response = await pythonVmInstance.get("/st_pictorial/products", {
		params: params,
		timeout: 300000, // 5 minutes to match backend
		signal,
	});

	return response.data;
};

/** Stable cache key for identical Apply queries (client-side result reuse). */
export const buildPictorialProductsCacheKey = (startDate, endDate, filtersObj = {}) => {
	const entries = Object.entries(filtersObj || {})
		.filter(
			([key, value]) =>
				key !== "start_date" &&
				key !== "end_date" &&
				key !== "sort_field" &&
				key !== "sort_direction" &&
				key !== "size" &&
				value &&
				String(value).trim() !== ""
		)
		.sort(([a], [b]) => a.localeCompare(b));
	return JSON.stringify({
		start: format(startDate, "yyyy-MM-dd"),
		end: format(endDate, "yyyy-MM-dd"),
		filters: entries,
	});
};

/**
 * POST /st_pictorial/barcode-facility-soh — barcode × facility WH/EBO SOH for CSV export.
 */
export const fetchPictorialBarcodeFacilitySoh = async (pkeys, { store } = {}) => {
	const uniquePkeys = [...new Set((pkeys || []).map((k) => String(k).trim()).filter(Boolean))];
	if (uniquePkeys.length === 0) return [];

	const response = await pythonVmInstance.post(
		"/st_pictorial/barcode-facility-soh",
		{
			pkeys: uniquePkeys,
			store: store || "",
		},
		{ timeout: 300000 }
	);

	return response?.data?.rows || [];
};

/** Full ITEM_MASTER PKEY used for barcode-level SOH lookups (BRAND-DEPT-STYLE-COLOR-PALETTE). */
export const getProductExportPkey = (product) => {
	if (!product) return "";
	if (product.PKEY_FULL && product.PKEY_FULL !== product.PKEY) {
		return product.PKEY_FULL;
	}
	const brand = product.brand || "";
	const dept = product.category || "";
	const style = product.style || "";
	const color = product.color || "";
	const palette = product.paletteType || product.palette || "";
	if (brand && dept && style && color && palette) {
		return `${brand}-${dept}-${style}-${color}-${palette}`;
	}
	return product.PKEY || "";
};
